import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Box, Paper, Card, CardContent, Typography, TextField, Button,
  Alert, CircularProgress, Stack
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { post } from '../../services/apiService';

const Signup = () => {
  const [form, setForm] = useState({ username: '', displayName: '', email: '', password: '', confirmPassword: '', signupReason: '' });
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const validate = () => {
    const errs = {};
    if (!form.username.trim()) errs.username = 'Username is required';
    else if (form.username.trim().length < 3) errs.username = 'At least 3 characters';
    if (!form.displayName.trim()) errs.displayName = 'Full name is required';
    if (!form.email.trim()) errs.email = 'Email is required';
    else if (!/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/.test(form.email)) errs.email = 'Enter a valid email';
    if (!form.password) errs.password = 'Password is required';
    else if (form.password.length < 6) errs.password = 'At least 6 characters';
    if (form.password !== form.confirmPassword) errs.confirmPassword = 'Passwords do not match';
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setServerError('');
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setErrors({});
    setLoading(true);
    try {
      await post('/api/auth/signup', {
        username: form.username.trim(),
        displayName: form.displayName.trim(),
        email: form.email.trim().toLowerCase(),
        password: form.password,
        signupReason: form.signupReason.trim() || undefined
      });
      setSubmitted(true);
    } catch (err) {
      setServerError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="100vh"
      sx={{ background: 'linear-gradient(135deg, #F8F9FA 0%, #E9ECEF 100%)' }}
    >
      <Box sx={{ maxWidth: 480, width: '100%', px: 2, py: 4 }}>
        <Paper elevation={4} sx={{ borderRadius: 3, overflow: 'hidden', border: '1px solid rgba(0,0,0,0.05)' }}>
          <Card sx={{ boxShadow: 'none' }}>
            <CardContent sx={{ p: 4 }}>
              {submitted ? (
                <Box textAlign="center" py={2}>
                  <CheckCircleOutlineIcon sx={{ fontSize: 56, color: '#1D4434', mb: 2 }} />
                  <Typography variant="h5" fontWeight={600} gutterBottom>Request Submitted</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.7 }}>
                    Your access request has been sent to the portal administrators for review.
                    You can sign in once an administrator approves your account.
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 3 }}>
                    This typically takes 1–2 business days.
                  </Typography>
                  <Button component={Link} to="/login" variant="outlined" color="primary" size="small">
                    Back to Sign In
                  </Button>
                </Box>
              ) : (
                <>
                  <Box textAlign="center" mb={3}>
                    <Typography variant="h5" fontWeight={600} sx={{ color: 'primary.main', letterSpacing: '-0.02em' }}>
                      Request Access
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      HighQ Metrics Portal — submit a request to get access
                    </Typography>
                  </Box>

                  {serverError && (
                    <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{serverError}</Alert>
                  )}

                  <form onSubmit={handleSubmit} noValidate>
                    <Stack spacing={2}>
                      <TextField
                        label="Full Name"
                        value={form.displayName}
                        onChange={set('displayName')}
                        fullWidth
                        size="small"
                        required
                        disabled={loading}
                        error={!!errors.displayName}
                        helperText={errors.displayName}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                      <TextField
                        label="Username"
                        value={form.username}
                        onChange={set('username')}
                        fullWidth
                        size="small"
                        required
                        disabled={loading}
                        error={!!errors.username}
                        helperText={errors.username}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                      <TextField
                        label="Work Email"
                        type="email"
                        value={form.email}
                        onChange={set('email')}
                        fullWidth
                        size="small"
                        required
                        disabled={loading}
                        error={!!errors.email}
                        helperText={errors.email}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                      <TextField
                        label="Password"
                        type="password"
                        value={form.password}
                        onChange={set('password')}
                        fullWidth
                        size="small"
                        required
                        disabled={loading}
                        error={!!errors.password}
                        helperText={errors.password}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                      <TextField
                        label="Confirm Password"
                        type="password"
                        value={form.confirmPassword}
                        onChange={set('confirmPassword')}
                        fullWidth
                        size="small"
                        required
                        disabled={loading}
                        error={!!errors.confirmPassword}
                        helperText={errors.confirmPassword}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                      <TextField
                        label="Reason for access (optional)"
                        value={form.signupReason}
                        onChange={set('signupReason')}
                        fullWidth
                        size="small"
                        multiline
                        rows={2}
                        disabled={loading}
                        inputProps={{ maxLength: 500 }}
                        placeholder="e.g. I'm joining the QA team and need access to sprint metrics"
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                      <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        fullWidth
                        size="large"
                        disabled={loading}
                        sx={{
                          py: 1.4, borderRadius: 2, fontSize: '1rem', fontWeight: 500,
                          textTransform: 'none', boxShadow: 2,
                          '&:hover': { boxShadow: 4, transform: 'translateY(-1px)' }
                        }}
                      >
                        {loading ? <CircularProgress size={22} color="inherit" /> : 'Submit Request'}
                      </Button>
                    </Stack>
                  </form>

                  <Box mt={2.5} textAlign="center">
                    <Typography variant="body2" color="text.secondary">
                      Already have an account?{' '}
                      <Link to="/login" style={{ color: 'inherit', fontWeight: 500 }}>Sign in</Link>
                    </Typography>
                  </Box>
                </>
              )}
            </CardContent>
          </Card>
        </Paper>
      </Box>
    </Box>
  );
};

export default Signup;
